import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"
import { createConsumer } from "@rails/actioncable"
import TextToSpeech from "../lib/text_to_speech"

const consumer2nd = createConsumer()

export default class extends Controller {
  static values = {
    discoveryId: Number,
    adminMode: Boolean,
    canRequestAudio: { type: Boolean, default: false },
    audioLimitTitle: { type: String, default: "Reached audio limit for today. Refreshes tomorrow." },
  }

  static targets = [
    "personaTab", "personaVersionSelector", "explanationTab", "explanationContainer", "explanationTitle",
    "explanationText", "explanationError", "explanationStructuredOutput", "explanationSources",
    "loader", "audioContainer", "audioElement", "playButton",
    "requestAudioButton", "regenerateAudioButton", "audioFailedBadge", "rewindButton", "forwardButton",
    "playbackSpeedControls", "ttsButton", "regenerateButton"
  ]

  connect() {
    this.state = {
      explanations: {},
      currentExplanationId: null,
      currentPersonaId: null,
      currentLanguage: null,
      currentPersonaVersionId: null,
      latestUpdateRedisId: null,
      audio: {
        latestUpdateRedisId: null,
        context: null,
        useAudioContext: !window.MediaSource || /iPhone|iPad|iPod/.test(navigator.userAgent),
        mediaSource: null,
        sourceBuffer: null,
        ttsPlaying: false,
      }
    }

    this.timing = {
      pageLoadStart: window.pageTimingStart || (window.performance.timeOrigin + window.performance.timing.navigationStart),
      controllerConnect: Date.now(),
      events: [],
      lastSentEventIndex: -1 // Track which events have been sent
    }

    this.addClientTiming("controller_connected", "Discovery channel controller connected")

    this.initializeExplanations()
    this.audioElementTargets.forEach((el) => this._configureAudioElement(el))
    this.initializeTTS()
    this.subscribeDiscoveryChannel()
    this.subscribeAudioStreamChannel()
    this.render()

    // Set up periodic timing data sending
    this.setupPeriodicTimingSend()
  }

  disconnect() {
    this.addClientTiming("controller_disconnect", "Discovery channel controller disconnected")

    // Clear timing interval
    if (this.timingSendInterval) {
      clearInterval(this.timingSendInterval)
    }

    // Send final timing data
    this.sendTimingDataToServer()

    // Clean up TTS module
    if (this.tts) {
      this.tts.destroy()
    }

    if (this.channel) this.channel.unsubscribe()
    if (this.audioStreamChannel) this.audioStreamChannel.unsubscribe()
    if (this.state.audio.context) this.state.audio.context.close()
  }

  // TTS Integration Methods
  // ----------------------------------------------------------------

  initializeTTS() {
    this.tts = new TextToSpeech({
      debug: true,
      autoDetectLanguage: true
    })

    // Listen to TTS events for UI updates and timing logs
    this.tts.addEventListener('tts:started', (event) => {
      this.state.audio.ttsPlaying = true
      this.addClientTiming("tts_started", `TTS playback started`, this.state.currentExplanationId)
      this.render()
    })

    this.tts.addEventListener('tts:stopped', (event) => {
      this.state.audio.ttsPlaying = false
      this.addClientTiming("tts_stopped", `TTS playback stopped`, this.state.currentExplanationId)
      this.render()
    })

    this.tts.addEventListener('tts:ended', (event) => {
      this.state.audio.ttsPlaying = false
      this.addClientTiming("tts_ended", `TTS playback ended`, this.state.currentExplanationId)
      this.render()
    })

    this.tts.addEventListener('tts:error', (event) => {
      this.state.audio.ttsPlaying = false
      this.addClientTiming("tts_error", `TTS error: ${event.detail.error}`, this.state.currentExplanationId)
      console.error('TTS Error:', event.detail.error)
      this.render()
    })

    this.tts.addEventListener('tts:language-detected', (event) => {
      this.addClientTiming("tts_language_detected", `Language detected: ${event.detail.language}`, this.state.currentExplanationId)
    })

    this.tts.addEventListener('tts:voice-selected', (event) => {
      this.addClientTiming("tts_voice_selected", `Voice selected: ${event.detail.voice.name} (${event.detail.voice.lang})`, this.state.currentExplanationId)
    })
  }

  shouldUseTTS() {
    const explanation = this.state.explanations[this.state.currentExplanationId]
    if (!explanation) return false

    return explanation.status === 'succeeded' &&
           !explanation.audio.hasNarration
  }

  toggleTTS() {
    const explanation = this.state.explanations[this.state.currentExplanationId]
    if (!explanation || !explanation.content) return

    if (this.tts.isPlaying()) {
      this.tts.stop()
    } else {
      this.tts.play(explanation.content, this.state.currentLanguage)
    }
  }

  // Timing Methods
  // ----------------------------------------------------------------

  addClientTiming(event, message, explanationId = null) {
    const timestamp = Date.now()
    this.timing.events.push({
      event,
      message,
      timestamp,
      explanationId,
      relativeTime: timestamp - this.timing.controllerConnect
    })
    
    // Also log to console for debugging
    console.log(`[Timing] ${event}: ${message} (${timestamp - this.timing.controllerConnect}ms)`)
  }

  setupPeriodicTimingSend() {
    // Check if there are any explanations that are not completed (not 'succeeded' or 'failed')
    const hasInProgressExplanations = Object.values(this.state.explanations).some(explanation => 
      explanation.status !== 'succeeded' && explanation.status !== 'failed'
    )
    
    if (hasInProgressExplanations) {
      // Send timing data every 10 seconds during active use
      this.timingSendInterval = setInterval(() => {
        if (this.timing.events.length > this.timing.lastSentEventIndex + 1) {
          this.sendTimingDataToServer(false) // Don't clear events for periodic sends
        }
      }, 10000)
    }
  }

  sendTimingDataToServer(clearEvents = true) {
    const unsentEvents = this.timing.events.slice(this.timing.lastSentEventIndex + 1)
    if (unsentEvents.length === 0) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (!csrfToken) return

    const dataToSend = {
      timing_data: {
        pageLoadStart: this.timing.pageLoadStart,
        controllerConnect: this.timing.controllerConnect,
        events: unsentEvents // Only send unsent events
      }
    }

    fetch(`/discoveries/${this.discoveryIdValue}/client_timing`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(dataToSend)
    }).then(response => {
      if (response.ok) {
        // Update the last sent index to current array length - 1
        this.timing.lastSentEventIndex = this.timing.events.length - 1
        
        if (clearEvents) {
          // Only clear events if this was a final send (on disconnect)
          this.timing.events = []
          this.timing.lastSentEventIndex = -1
        }
      }
    }).catch(error => {
      console.error('Failed to send timing data:', error)
    })
  }

  // Setup and State Management
  // ----------------------------------------------------------------

  initializeExplanations() {
    this.explanationContainerTargets.forEach(container => {
      const id = container.dataset.explanationId
      const audioContainer = this.audioContainerTargets.find(ac => ac.closest('.explanation-container') === container)

      this.state.explanations[id] = {
        id: id,
        personaId: container.dataset.personaId,
        personaVersionId: container.dataset.personaVersionId,
        status: container.dataset.status, // 'pending', 'succeeded', 'failed'
        content: container.dataset.explanationPlainText || null,
        title: null,
        error: null,
        structuredOutput: null,
        sourceAnnotations: this._parseSourceAnnotationsFromDataset(container),
        webSearchEnabled: container.dataset.webSearchEnabled === 'true',
        audio: {
          hasNarration: audioContainer.dataset.hasNarration === 'true',
          streamRequested: audioContainer.dataset.audioStreamRequested === 'true',
          status: 'idle', // 'idle', 'loading', 'ready', 'playing', 'failed'
          bufferQueue: [],
          nextBufferIndex: 0,
          isPlaying: false,
          currentTime: 0,
          processing: false,
          autoPlay: false,
          hasAutoStarted: false,
        }
      }
    })

    // Initialize current persona and version from active tab and visible container
    const activePersonaTab = this.personaTabTargets.find(t => t.parentElement.classList.contains('is-active'))
    this.state.currentPersonaId = activePersonaTab?.dataset.personaId
    this.state.currentLanguage = activePersonaTab?.dataset.language

    const visibleContainer = this.explanationContainerTargets.find(c => !c.classList.contains('is-hidden'))
    this.state.currentPersonaVersionId = visibleContainer?.dataset.personaVersionId
    this.state.currentExplanationId = visibleContainer?.dataset.explanationId
  }

  _findExplanationForPersonaAndVersion(personaId, personaVersionId) {
    return Object.values(this.state.explanations).find(exp =>
      exp.personaId === personaId && exp.personaVersionId === personaVersionId
    )
  }

  _findFallbackExplanationForPersona(personaId) {
    const explanations = Object.values(this.state.explanations).filter(exp => exp.personaId === personaId)
    if (explanations.length === 0) return null

    // Prefer the explanation that is currently visible, if any
    const currentExplanation = this.state.currentExplanationId && explanations.find(exp => exp.id === this.state.currentExplanationId)
    if (currentExplanation) return currentExplanation

    // Otherwise, return the first explanation for this persona
    return explanations[0]
  }

  subscribeDiscoveryChannel() {
    this.addClientTiming("discovery_channel_subscribe_start", "Starting discovery channel subscription")
    
    this.channel = consumer.subscriptions.create(
      { channel: "DiscoveryChannel", discovery_id: this.discoveryIdValue },
      {
        connected: () => {
          this.addClientTiming("discovery_channel_connected", "Discovery channel connected")
          this.channel.perform("request_data", { latest_update_redis_id: this.state.latestUpdateRedisId })
          this.addClientTiming("discovery_channel_request_data", "Requested initial data from discovery channel")
        },
        received: (data) => {
          this.addClientTiming("discovery_channel_data_received", `Received data: ${data.type} for explanation ${data.eid}`, data.eid)
          this.processDiscoveryData(data)
        },
        disconnected: () => {
          this.addClientTiming("discovery_channel_disconnected", "Discovery channel disconnected")
        },
        rejected: () => {
          this.addClientTiming("discovery_channel_rejected", "Discovery channel subscription rejected")
        }
      }
    )
  }

  subscribeAudioStreamChannel() {
    this.addClientTiming("audio_channel_subscribe_start", "Starting audio stream channel subscription")
    
    this.audioStreamChannel = consumer2nd.subscriptions.create(
      { channel: "AudioStreamChannel", discovery_id: this.discoveryIdValue },
      {
        connected: () => {
          this.addClientTiming("audio_channel_connected", "Audio stream channel connected")
        },
        received: (data) => {
          this.addClientTiming("audio_channel_data_received", `Received audio data: ${data.type} for explanation ${data.explanation_id}`, data.explanation_id)
          this.processAudioData(data)
        },
        disconnected: () => {
          this.addClientTiming("audio_channel_disconnected", "Audio stream channel disconnected")
        },
        rejected: () => {
          this.addClientTiming("audio_channel_rejected", "Audio stream channel subscription rejected")
        }
      }
    )
  }

  // Data Processing
  // ----------------------------------------------------------------

  processDiscoveryData(data) {
    console.log("Received text data:", data)
    this.state.latestUpdateRedisId = data.redis_id
    const explanation = this.state.explanations[data.eid]
    if (!explanation) return

    if (!this.firstDataReceived) {
      this.firstDataReceived = true
      this.addClientTiming("first_data_received", `First data received for explanation ${data.eid}`, data.eid)
      setTimeout(() => {
        this.personaTabTargets[0]?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 1000);
    }

    if (data.type === 'final') {
      this.addClientTiming("explanation_final_received", `Final data received for explanation ${data.eid}`, data.eid)
      const json = JSON.parse(data.content)
      if (json.error) {
        explanation.status = 'failed'
        explanation.error = `Explanation failed to generate: ${json.error || "An unknown error occurred."}`
        this.addClientTiming("explanation_failed", `Explanation ${data.eid} failed: ${json.error}`, data.eid)
      } else {
        explanation.status = 'succeeded'
        explanation.title = json.ai_metadata?.title
        explanation.structuredOutput = json
        explanation.webSearchEnabled = json.web_search_enabled === true
        explanation.sourceAnnotations = json.source_annotations || []
        if (json.explanation_text) {
          explanation.content = json.explanation_text
          const container = this.explanationContainerTargets.find(c => c.dataset.explanationId === data.eid)
          const text = this._getTargetsForContainer(container).text
          if (text) text.textContent = json.explanation_text
        }
        this.addClientTiming("explanation_completed", `Explanation ${data.eid} completed successfully`, data.eid)
      }
    } else {
      if (['succeeded', 'failed'].includes(explanation.status)) return

      explanation.status = 'streaming'
      explanation.content = data.content
      this.addClientTiming("explanation_update_received", `Content update received for explanation ${data.eid} (${data.content?.length || 0} chars)`, data.eid)
    }
    this.render()
  }

  processAudioData(data) {
    console.log("Received audio data:", data)
    if (data.explanation_id !== this.state.currentExplanationId) return

    this.state.audio.latestUpdateRedisId = data.redis_id
    const audioState = this.state.explanations[data.explanation_id].audio

    if (data.type === 'audio_data') {
      if (data.status === 'failed') {
        audioState.status = 'failed'
      } else if (data.status === 'succeeded') {
        this.handleAudioChunk(data)
      }
    }
    this.render()
  }

  // Element Finders
  // ----------------------------------------------------------------

  _getTargetsForContainer(container) {
    if (!container) return {}
    return {
      title: this.explanationTitleTargets.find(t => t.closest('.explanation-container') === container),
      text: this.explanationTextTargets.find(t => t.closest('.explanation-container') === container),
      error: this.explanationErrorTargets.find(e => e.closest('.explanation-container') === container),
      structuredOutput: this.explanationStructuredOutputTargets.find(s => s.closest('.explanation-container') === container),
      sources: this.explanationSourcesTargets.find(s => s.closest('.explanation-container') === container),
      loader: this.loaderTargets.find(l => l.closest('.explanation-container') === container),
      audioContainer: this.audioContainerTargets.find(ac => ac.closest('.explanation-container') === container),
      playButton: this.playButtonTargets.find(b => b.closest('.explanation-container') === container),
      requestButton: this.requestAudioButtonTargets.find(b => b.closest('.explanation-container') === container),
      failedBadge: this.audioFailedBadgeTargets.find(b => b.closest('.explanation-container') === container),
      rewindButton: this.rewindButtonTargets.find(b => b.closest('.explanation-container') === container),
      forwardButton: this.forwardButtonTargets.find(b => b.closest('.explanation-container') === container),
      speedControls: this.playbackSpeedControlsTargets.find(b => b.closest('.explanation-container') === container),
      audioElement: this.audioElementTargets.find(el => el.closest('.explanation-container') === container),
      ttsButton: this.ttsButtonTargets.find(b => b.closest('.explanation-container') === container),
      regenerateButton: this.regenerateButtonTargets.find(b => b.closest('.explanation-container') === container),
      regenerateAudioButton: this.regenerateAudioButtonTargets.find(b => b.closest('.explanation-container') === container),
    }
  }

  get _currentAudioElement() {
    const container = this.explanationContainerTargets.find(c => c.dataset.explanationId === this.state.currentExplanationId)
    return this._getTargetsForContainer(container).audioElement
  }

  // Rendering
  // ----------------------------------------------------------------

  render() {
    this.explanationContainerTargets.forEach(container => {
      const id = container.dataset.explanationId
      const explanation = this.state.explanations[id]
      const isCurrent = id === this.state.currentExplanationId

      container.classList.toggle('is-hidden', !isCurrent)
      if (!isCurrent) return // Don't render elements that are hidden

      const { title, text, error, structuredOutput, sources, loader, audioContainer, regenerateButton } = this._getTargetsForContainer(container)

      if (title && explanation.title) title.textContent = explanation.title
      if (text && explanation.content != null && explanation.status === 'streaming') {
        text.textContent = explanation.content
      }
      this.renderSourceAnnotations(explanation, sources)
      if (error && explanation.error) {
        error.textContent = explanation.error
        error.classList.toggle('is-hidden', false)
        regenerateButton.classList.toggle('is-hidden', false)
      }
      if (structuredOutput && explanation.structuredOutput) {
        const { explanation_text: _explanationText, ...metadataForDisplay } = explanation.structuredOutput
        structuredOutput.innerHTML = `<pre>${JSON.stringify(metadataForDisplay, null, 2)}</pre>`
      }

      // Render visibility of elements
      if (loader) loader.classList.toggle('is-hidden', explanation.status !== 'pending' && explanation.status !== 'streaming')
      if (audioContainer) audioContainer.classList.toggle('is-hidden', explanation.status === 'failed')

      this.renderAudioState(explanation, container)
    })

    // Render persona tab states
    this.personaTabTargets.forEach(tab => {
      const personaId = tab.dataset.personaId
      tab.parentElement.classList.toggle('is-active', personaId === this.state.currentPersonaId)
    })

    // Update persona version selector visibility and values
    this.personaVersionSelectorTargets.forEach(selector => {
      const selectorPersonaId = selector.dataset.personaId
      const shouldShow = selectorPersonaId === this.state.currentPersonaId
      const levelElement = selector.closest('.level')
      if (levelElement) {
        levelElement.style.display = shouldShow ? '' : 'none'
      }

      if (shouldShow) {
        selector.value = this.state.currentPersonaVersionId
      }
    })

    // Render explanation tab states (for backward compatibility)
    this.explanationTabTargets.forEach(tab => {
      const id = tab.dataset.explanationId
      tab.parentElement.classList.toggle('is-active', id === this.state.currentExplanationId)
    })
  }

  _parseSourceAnnotationsFromDataset(container) {
    try {
      return JSON.parse(container.dataset.sourceAnnotations || '[]')
    } catch (_error) {
      return []
    }
  }

  renderSourceAnnotations(explanation, sourcesEl) {
    if (!sourcesEl || !explanation.webSearchEnabled) return

    if (explanation.status !== 'succeeded') return

    sourcesEl.classList.remove('is-hidden')

    const annotations = this._sourceAnnotationsForDisplay(
      explanation.sourceAnnotations || explanation.structuredOutput?.source_annotations || []
    )
    const summary = sourcesEl.querySelector('summary')
    if (summary) summary.textContent = `Sources (${annotations.length})`

    const body = sourcesEl.querySelector('.explanation-sources-body')
    if (!body) return

    if (annotations.length === 0) {
      body.innerHTML = '<p class="is-size-7 has-text-grey">No web sources were cited for this explanation.</p>'
      return
    }

    const items = annotations.map(cite => {
      const label = cite.title || cite.url
      const snippet = cite.content ? `<p class="is-size-7 has-text-grey mt-1">${this._escapeHtml(cite.content.slice(0, 200))}</p>` : ''
      const statusBadge = this._sourceAnnotationStatusBadge(cite)
      return `<li class="mb-1"><a href="${this._escapeHtml(cite.url)}" target="_blank" rel="noopener noreferrer" class="has-text-link">${this._escapeHtml(label)}</a>${statusBadge}${snippet}</li>`
    }).join('')
    body.innerHTML = `<ul>${items}</ul>`
  }

  _sourceAnnotationsForDisplay(annotations) {
    if (this.adminModeValue) return annotations
    return annotations.filter(cite => !cite.link_status || cite.link_status === 'ok')
  }

  _sourceAnnotationStatusBadge(cite) {
    if (!this.adminModeValue) return ''
    const label = cite.link_status === 'not_found' ? '404' : cite.link_status === 'bad' ? 'bad link' : ''
    if (!label) return ''
    return `<span class="tag is-danger is-small ml-1">${this._escapeHtml(label)}</span>`
  }

  _escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  renderAudioState(explanation, container) {
    const audioState = explanation.audio
    const { playButton, requestButton, regenerateAudioButton, failedBadge, rewindButton, forwardButton, speedControls, ttsButton } = this._getTargetsForContainer(container)

    const showPlay = audioState.hasNarration || (audioState.streamRequested && audioState.status !== 'failed')
    const showRequest = !showPlay
    const showTTS = this.shouldUseTTS()
    const showControls = showPlay && (audioState.hasNarration || !this.state.audio.useAudioContext) && audioState.status !== 'loading'

    if (playButton) {
      playButton.classList.toggle('is-hidden', !showPlay)
      if (showPlay) {
        let iconClass = 'fa-play'
        let disabled = false
        switch (audioState.status) {
          case 'playing': iconClass = 'fa-pause'; break;
          case 'loading': iconClass = 'fa-spinner fa-spin'; disabled = true; break;
        }
        playButton.disabled = disabled
        playButton.innerHTML = `<span class="icon"><i class="fas ${iconClass}"></i></span>`
      }
    }

    if (ttsButton) {
      ttsButton.classList.toggle('is-hidden', !showTTS)
      if (showTTS) {
        let iconClass = 'fa-play'
        let buttonText = 'robot voice'
        if (this.state.audio.ttsPlaying) {
          iconClass = 'fa-pause'
          buttonText = 'robot voice'
        }
        ttsButton.innerHTML = `<span class="icon"><i class="fas ${iconClass}"></i></span><span>${buttonText}</span>`
      }
    }

    if (rewindButton) rewindButton.classList.toggle('is-hidden', !showControls)
    if (forwardButton) forwardButton.classList.toggle('is-hidden', !showControls)
    if (speedControls) speedControls.classList.toggle('is-hidden', !showControls)
    if (requestButton) {
      requestButton.classList.toggle('is-hidden', !showRequest)
      if (showRequest) {
        requestButton.disabled = !this.canRequestAudioValue
        requestButton.title = this.canRequestAudioValue ? "" : this.audioLimitTitleValue
      }
    }
    if (failedBadge) {
      failedBadge.classList.toggle('is-hidden', audioState.status !== 'failed')
      if (audioState.status === 'failed' && audioState.errorMessage) {
        const label = failedBadge.querySelector('span:last-child')
        if (label) label.textContent = audioState.errorMessage
      }
    }
    if (regenerateAudioButton) {
      regenerateAudioButton.disabled = audioState.status === 'loading'
    }
  }


  // User Actions
  // ----------------------------------------------------------------

  generateExplanationForPersonaVersion(personaVersionId) {
    this.addClientTiming("generate_explanation_start", `Generating explanation for persona version ${personaVersionId}`, null)

    const form = document.getElementById('generate-explanation-form')
    const personaVersionIdField = document.getElementById('generate-explanation-persona-version-id')

    if (form && personaVersionIdField) {
      personaVersionIdField.value = personaVersionId
      form.requestSubmit()
    } else {
      console.error('Generate explanation form not found')
      this.addClientTiming("generate_explanation_error", "Generate explanation form not found", null)
    }
  }

  switchPersona(event) {
    event.preventDefault()
    const newPersonaId = event.currentTarget.dataset.personaId
    const newLanguage = event.currentTarget.dataset.language
    if (newPersonaId === this.state.currentPersonaId) return

    this.addClientTiming("persona_tab_switch", `Switched to persona ${newPersonaId}`)

    // Pause all audio
    this.audioElementTargets.forEach(el => {
      if (el.src) {
        el.pause()
        el.currentTime = 0
      }
    })
    if (this.state.audio.context && this.state.audio.context.state === 'running') {
      this.state.audio.context.suspend()
    }

    // Stop TTS if playing
    if (this.tts) {
      this.tts.stop()
    }

    this.state.currentPersonaId = newPersonaId
    this.state.currentLanguage = newLanguage

    // Find the active version selector for this persona and set current version
    const versionSelector = this.personaVersionSelectorTargets.find(s => s.dataset.personaId === newPersonaId)
    let explanation = null
    
    if (versionSelector) {
      this.state.currentPersonaVersionId = versionSelector.value
      explanation = this._findExplanationForPersonaAndVersion(newPersonaId, this.state.currentPersonaVersionId)
    }
    
    if (!explanation) {
      explanation = this._findFallbackExplanationForPersona(newPersonaId)
      if (explanation) {
        this.state.currentPersonaVersionId = explanation.personaVersionId
        this.state.currentExplanationId = explanation.id
      }
    }
    
    if (explanation) {
      this.state.currentExplanationId = explanation.id
      if (explanation.audio.streamRequested && explanation.audio.status === 'idle') {
        this.startAudioStream()
      }
    }

    // Update URL to include persona_id parameter
    const url = new URL(window.location)
    url.searchParams.set('persona_id', newPersonaId)
    window.history.replaceState({}, '', url)

    this.render()
  }

  switchPersonaVersion(event) {
    const newVersionId = event.currentTarget.value
    if (newVersionId === this.state.currentPersonaVersionId) return

    this.addClientTiming("persona_version_switch", `Switched to persona version ${newVersionId}`)

    // Pause all audio
    this.audioElementTargets.forEach(el => {
      if (el.src) {
        el.pause()
        el.currentTime = 0
      }
    })
    if (this.state.audio.context && this.state.audio.context.state === 'running') {
      this.state.audio.context.suspend()
    }

    // Stop TTS if playing
    if (this.tts) {
      this.tts.stop()
    }

    this.state.currentPersonaVersionId = newVersionId

    // Find the explanation for this persona + version combination
    const explanation = this._findExplanationForPersonaAndVersion(this.state.currentPersonaId, newVersionId)

    if (explanation) {
      this.state.currentExplanationId = explanation.id
      if (explanation.audio.streamRequested && explanation.audio.status === 'idle') {
        this.startAudioStream()
      }
    } else {
      // No explanation exists for this persona version, generate one
      this.generateExplanationForPersonaVersion(newVersionId)
    }

    this.render()
  }

  switchExplanation(event) {
    event.preventDefault()
    const newId = event.currentTarget.dataset.explanationId
    if (newId === this.state.currentExplanationId) return

    this.addClientTiming("explanation_tab_switch", `Switched to explanation ${newId}`, newId)

    // Pause all audio
    this.audioElementTargets.forEach(el => {
      if (el.src) {
        el.pause()
        el.currentTime = 0
      }
    })
    if (this.state.audio.context && this.state.audio.context.state === 'running') {
      this.state.audio.context.suspend()
    }

    // Stop TTS if playing
    if (this.tts) {
      this.tts.stop()
    }

    const currentExplanation = this.state.explanations
    currentExplanation[this.state.currentExplanationId].audio.status = 'idle'

    this.state.currentExplanationId = newId
    const explanation = this.state.explanations[newId]
    if (explanation.audio.streamRequested && explanation.audio.status === 'idle') {
      this.startAudioStream()
    }
    this.render()
  }

  requestAudio(event) {
    event.preventDefault()
    if (!this.canRequestAudioValue || event.currentTarget.disabled) return
    this._postAudioStreamRequest(event.currentTarget.dataset.url, "audio_request")
  }

  regenerateAudio(event) {
    event.preventDefault()
    if (event.currentTarget.disabled) return

    const explanation = this.state.explanations[this.state.currentExplanationId]
    const audioElement = this._currentAudioElement
    if (audioElement) {
      audioElement.pause()
      audioElement.removeAttribute('src')
      audioElement.load()
    }
    explanation.audio.hasNarration = false
    explanation.audio.status = 'idle'
    explanation.audio.bufferQueue = []
    explanation.audio.nextBufferIndex = 0

    this._postAudioStreamRequest(event.currentTarget.dataset.url, "audio_regenerate")
  }

  _postAudioStreamRequest(url, timingPrefix) {
    const explanation = this.state.explanations[this.state.currentExplanationId]
    explanation.audio.streamRequested = true
    explanation.audio.errorMessage = null
    explanation.audio.autoPlay = true
    explanation.audio.hasAutoStarted = false
    explanation.audio.status = 'loading'

    this.addClientTiming(`${timingPrefix}_start`, `Audio stream requested for explanation ${this.state.currentExplanationId}`, this.state.currentExplanationId)
    this.render()

    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ explanation_id: this.state.currentExplanationId })
    }).then(async response => {
      if (response.ok) {
        this.addClientTiming(`${timingPrefix}_success`, `Audio stream request succeeded for explanation ${this.state.currentExplanationId}`, this.state.currentExplanationId)
        this.startAudioStream()
      } else {
        let message = response.statusText
        try {
          const body = await response.json()
          if (body.message) message = body.message
        } catch (_e) { /* non-JSON error body */ }
        this.addClientTiming(`${timingPrefix}_failed`, `Audio stream request failed for explanation ${this.state.currentExplanationId}: ${message}`, this.state.currentExplanationId)
        explanation.audio.status = 'failed'
        explanation.audio.errorMessage = message
        if (response.status === 403) {
          this.canRequestAudioValue = false
        }
        this.render()
      }
    }).catch(() => {
      this.addClientTiming(`${timingPrefix}_error`, `Audio stream request error for explanation ${this.state.currentExplanationId}`, this.state.currentExplanationId)
      explanation.audio.status = 'failed'
      this.render()
    })
  }

  async playAudio() {
    const explanation = this.state.explanations[this.state.currentExplanationId]
    const audioState = explanation.audio
    const audioElement = this._currentAudioElement

    if (!audioElement) return

    this.addClientTiming("audio_play_pause", `Audio ${audioElement.paused ? 'play' : 'pause'} for explanation ${this.state.currentExplanationId}`, this.state.currentExplanationId)

    if (audioState.hasNarration || !this.state.audio.useAudioContext) {
      if (audioElement.paused) {
        audioElement.play()
        audioState.status = 'playing'
        audioElement.onended = () => {
          audioState.status = 'ready'
          this.addClientTiming("audio_ended", `Audio playback ended for explanation ${this.state.currentExplanationId}`, this.state.currentExplanationId)
          this.render()
        }
      } else {
        audioElement.pause()
        audioState.status = 'ready'
      }
    } else if (this.state.audio.useAudioContext) {
      if (audioState.isPlaying) {
        this.state.audio.context.suspend()
        audioState.isPlaying = false
        audioState.status = 'ready'
      } else if (audioState.bufferQueue.length > 0) {
        if (this.state.audio.context.currentTime >= audioState.currentTime) {
          // All sources have finished playing — re-create from bufferQueue
          this.state.audio.context.close()
          this.state.audio.context = new (window.AudioContext || window.webkitAudioContext)()
          audioState.currentTime = 0
          for (const bytes of audioState.bufferQueue) {
            await this.addBufferToAudioContext(bytes)
          }
        } else {
          this.state.audio.context.resume()
        }
        audioState.isPlaying = true
        audioState.status = 'playing'
      }
    }
    this.render()
  }

  rewindAudio() {
    const audioElement = this._currentAudioElement
    if (audioElement) audioElement.currentTime = Math.max(0, audioElement.currentTime - 5)
  }

  forwardAudio() {
    const audioElement = this._currentAudioElement
    if (audioElement) audioElement.currentTime = Math.min(audioElement.duration, audioElement.currentTime + 5)
  }

  setPlaybackSpeed(event) {
    const speed = parseFloat(event.currentTarget.dataset.speed)
    const container = event.currentTarget.closest('.explanation-container')
    const { audioElement, speedControls } = this._getTargetsForContainer(container)

    if (audioElement) audioElement.playbackRate = speed

    if (speedControls) {
      speedControls.querySelectorAll('button').forEach(button => {
        button.classList.toggle('is-active', parseFloat(button.dataset.speed) === speed)
      })
    }
  }

  // Audio Streaming Logic
  // ----------------------------------------------------------------

  startAudioStream() {
    const explanation = this.state.explanations[this.state.currentExplanationId]
    const audioState = explanation.audio
    audioState.status = 'loading'
    audioState.bufferQueue = []
    audioState.nextBufferIndex = 0

    this.addClientTiming("audio_stream_start", `Starting audio stream for explanation ${explanation.id}`, explanation.id)

    if (this.state.audio.useAudioContext) {
      if (this.state.audio.context && this.state.audio.context.state !== 'closed') {
        this.state.audio.context.close()
      }
      this.state.audio.context = new (window.AudioContext || window.webkitAudioContext)()
      if (audioState.autoPlay) {
        this.state.audio.context.resume()
      } else {
        this.state.audio.context.suspend()
      }
      audioState.currentTime = 0
    } else {
      this.state.audio.mediaSource = null
      this.state.audio.sourceBuffer = null
    }

    this.audioStreamChannel.perform("request_audio_stream", {
      explanation_id: explanation.id,
      latest_update_redis_id: this.state.audio.latestUpdateRedisId
    })
    this.render()
  }

  async handleAudioChunk(data) {
    this.addClientTiming("audio_chunk_received", `Audio chunk ${data.index} received for explanation ${data.explanation_id}`, data.explanation_id)
    
    const base64 = data.audio_data
    const binary = atob(base64)
    const len = binary.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) { bytes[i] = binary.charCodeAt(i) }

    const audioState = this.state.explanations[data.explanation_id].audio
    audioState.bufferQueue[data.index] = bytes

    if (this.state.audio.useAudioContext) {
      if (audioState.processing) return
      audioState.processing = true

      while (audioState.bufferQueue[audioState.nextBufferIndex]) {
        await this.addBufferToAudioContext(audioState.bufferQueue[audioState.nextBufferIndex])
        audioState.nextBufferIndex++
      }
      if (audioState.nextBufferIndex > 0) {
        this._markAudioChunkReady(audioState, data.explanation_id)
      }
      audioState.processing = false
    } else {
      await this.initMediaSource()
      this.tryAppendNextBuffer()
      if (audioState.nextBufferIndex > 0) {
        this._markAudioChunkReady(audioState, data.explanation_id)
      }
    }
    this.render()
  }

  _markAudioChunkReady(audioState, explanationId) {
    const firstChunk = audioState.nextBufferIndex === 1
    if (firstChunk) {
      this.addClientTiming("audio_first_chunk_ready", `First audio chunk ready for explanation ${explanationId}`, explanationId)
    }

    if (audioState.autoPlay && firstChunk) {
      audioState.status = 'playing'
      this._tryAutoStartAudio(explanationId)
    } else if (audioState.status !== 'playing') {
      audioState.status = 'ready'
    }
  }

  _tryAutoStartAudio(explanationId) {
    const explanation = this.state.explanations[explanationId]
    if (!explanation || explanation.id !== this.state.currentExplanationId) return

    const audioState = explanation.audio
    if (!audioState.autoPlay || audioState.hasAutoStarted || audioState.status === 'failed') return

    audioState.hasAutoStarted = true

    if (audioState.hasNarration || !this.state.audio.useAudioContext) {
      const audioElement = this._currentAudioElement
      if (!audioElement) return

      const startPlayback = () => {
        audioElement.play()
          .then(() => {
            audioState.status = 'playing'
            audioElement.onended = () => {
              audioState.status = 'ready'
              this.render()
            }
            this.addClientTiming("audio_autoplay_started", `Auto-play started for explanation ${explanationId}`, explanationId)
            this.render()
          })
          .catch((err) => {
            console.warn('Auto-play blocked:', err)
            audioState.hasAutoStarted = false
            audioState.status = 'ready'
            this.render()
          })
      }

      if (audioElement.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        startPlayback()
      } else {
        audioElement.addEventListener('canplay', startPlayback, { once: true })
      }
    } else if (audioState.bufferQueue[0]) {
      audioState.isPlaying = true
      audioState.status = 'playing'
      this.state.audio.context.resume()
        .then(() => {
          this.addClientTiming("audio_autoplay_started", `Auto-play started for explanation ${explanationId}`, explanationId)
          this.render()
        })
        .catch((err) => {
          console.warn('Auto-play blocked:', err)
          audioState.hasAutoStarted = false
          audioState.isPlaying = false
          audioState.status = 'ready'
          this.render()
        })
    }
  }

  async addBufferToAudioContext(bytes) {
    const audioState = this.state.explanations[this.state.currentExplanationId].audio
    const buffer = await this.state.audio.context.decodeAudioData(bytes.buffer.slice())
    const source = this.state.audio.context.createBufferSource()
    source.buffer = buffer
    source.connect(this.state.audio.context.destination)
    
    source.onended = () => {
      if (this.state.audio.context.currentTime >= audioState.currentTime) {
        audioState.isPlaying = false
        audioState.status = 'ready'
        this.render()
      }
    }
    
    const startTime = Math.max(audioState.currentTime, this.state.audio.context.currentTime + 0.05)
    source.start(startTime)
    audioState.currentTime = startTime + buffer.duration
  }

  _configureAudioElement(audioElement) {
    if (!audioElement) return
    audioElement.controls = false
    audioElement.removeAttribute('controls')
  }

  async initMediaSource() {
    if (this.state.audio.mediaSource) return
    const audioElement = this._currentAudioElement
    if (!audioElement) return

    this._configureAudioElement(audioElement)
    this.state.audio.mediaSource = new MediaSource()
    audioElement.src = URL.createObjectURL(this.state.audio.mediaSource)

    return new Promise((resolve) => {
      this.state.audio.mediaSource.addEventListener('sourceopen', () => {
        if (!this.state.audio.sourceBuffer) {
          this.state.audio.sourceBuffer = this.state.audio.mediaSource.addSourceBuffer('audio/mpeg')
        }
        resolve()
      }, { once: true })
    })
  }

  tryAppendNextBuffer() {
    const { sourceBuffer } = this.state.audio
    if (!sourceBuffer || sourceBuffer.updating) return

    const audioState = this.state.explanations[this.state.currentExplanationId].audio
    const nextBuffer = audioState.bufferQueue[audioState.nextBufferIndex]

    if (nextBuffer) {
      sourceBuffer.appendBuffer(nextBuffer)
      audioState.nextBufferIndex++
      sourceBuffer.addEventListener('updateend', () => this.tryAppendNextBuffer(), { once: true })
    }
  }

}
